import { ArrowLeft } from 'lucide-react';
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
  showBackButton?: boolean;
  backTo?: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  subtitle,
  showBackButton = true,
  backTo = '/',
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(backTo);
  };

  return (
    <div className="min-h-screen flex font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Left side - Orange background with decorative elements */}
      <div className="flex-1 bg-[var(--brand-primary)] flex items-center justify-center p-6 lg:p-12 relative overflow-hidden">
        {/* Background decorative circles - Top Left */}
        <div className="absolute -top-32 -left-32 w-96 h-96 lg:w-[839px] lg:h-[839px] border-[25px] lg:border-[50px] border-white border-opacity-30 rounded-full backdrop-blur-[10px]"></div>
        <div className="absolute -top-20 -left-20 w-80 h-80 lg:w-[671px] lg:h-[671px] border-[25px] lg:border-[50px] border-white border-opacity-30 rounded-full backdrop-blur-[10px]"></div>

        {/* Background decorative circles - Bottom Right */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 lg:w-[839px] lg:h-[839px] border-[25px] lg:border-[50px] border-[var(--brand-primary)] border-opacity-5 rounded-full backdrop-blur-[10px]"></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 lg:w-[671px] lg:h-[671px] border-[25px] lg:border-[50px] border-[var(--brand-primary)] border-opacity-5 rounded-full backdrop-blur-[10px]"></div>

        {/* Glass card with content */}
        <div className="relative z-10 w-full max-w-lg lg:max-w-[760px]">
          <div className="bg-white bg-opacity-20 backdrop-blur-[26px] rounded-2xl p-6 lg:p-16 mb-8">
            <h1 className="text-3xl lg:text-[52px] font-bold text-white mb-4 leading-[1.3846153846153846em] tracking-[-2%]">
              Connected Construction : Top Technology Trends
            </h1>

            {/* Construction image */}
            <div className="mt-8 lg:mt-12">
              <img
                src="/images/hero-image.png"
                alt="Modern Construction House"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form content */}
      <div className="flex-1 bg-white flex items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full max-w-[560px]">
          {/* Back button and header */}
          <div className="mb-6 lg:mb-8">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="p-3 bg-[var(--brand-primary-light)] hover:bg-[var(--brand-primary)] hover:text-white rounded-md transition-colors mb-6"
              >
                <ArrowLeft size={20} className="text-[var(--brand-primary)] hover:text-white" />
              </button>
            )}

            <h2 className="text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-2 leading-[1.3333333333333333em]">
              {title}
            </h2>
            <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">{subtitle}</p>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
